import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useToast } from './ui';
import QuickView from './QuickView';
import ProductImage from './ProductImage';
import MarketCompare from './MarketCompare';
import useApprovalStatus from '../hooks/useApprovalStatus';
import './ProductCard.css';

// F5: Lifecycle / campaign badge derivation. Campaign tags that used to
// pollute the sidebar filter list now surface as small pills on each card
// so customers see relevant signals (NEW, PRE-ORDER, RESTOCK, CLEARANCE,
// LAST CHANCE) without us cluttering the navigation taxonomy.
const LIFECYCLE_BADGES = [
  { slug: 'pre-order',     label: 'Pre-order',  className: 'badge--preorder' },
  { slug: 'coming-soon',   label: 'Coming soon', className: 'badge--coming-soon' },
  { slug: 'pre-release',   label: 'Pre-release', className: 'badge--preorder' },
  { slug: 'new-arrival',   label: 'New',         className: 'badge--new' },
  { slug: 'restock',       label: 'Restocked',   className: 'badge--restock' },
  { slug: 'clearance',     label: 'Clearance',   className: 'badge--clearance' },
  { slug: 'last-chance',   label: 'Last chance', className: 'badge--last-chance' },
  { slug: 'black-friday',  label: 'Black Friday',className: 'badge--bf' },
  { slug: 'holiday',       label: 'Holiday',     className: 'badge--holiday' },
];

const ProductCard = ({ product, variant }) => {
  const { addToCart } = useCart();
  const toast = useToast();
  const { isApproved, isLoggedIn } = useApprovalStatus();
  const [qvOpen, setQvOpen] = useState(false);

  const onSale = !!product.compare_at_price && Number(product.compare_at_price) > Number(product.price);
  const discountPct = onSale
    ? Math.round((1 - product.price / product.compare_at_price) * 100)
    : 0;

  // Derive which lifecycle badges to show. Reads either `product.tags`
  // (array of slug strings) or `product.tag_slugs` if the API has changed.
  // Stock-based signals are auto-derived without needing a tag.
  const productTags = new Set([
    ...(Array.isArray(product.tags) ? product.tags.map(t => t?.slug || t) : []),
    ...(Array.isArray(product.tag_slugs) ? product.tag_slugs : []),
  ].filter(Boolean));
  const lifecycleBadges = LIFECYCLE_BADGES.filter(b => productTags.has(b.slug));
  // Auto-badge: "Last chance" when stock 1-3 and not already tagged.
  if (!productTags.has('last-chance') && product.stock > 0 && product.stock <= 3) {
    lifecycleBadges.push({ label: `Only ${product.stock} left`, className: 'badge--last-chance' });
  }

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.variants && product.variants.length > 0) {
      // Has variants — open QuickView so the customer can pick a condition
      setQvOpen(true);
      return;
    }
    addToCart(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQvOpen(true);
  };

  return (
    <>
      <Link to={`/product/${product.id}`} className={`product-card${variant === 'list' ? ' product-card--list' : ''}`}>
        <div className="product-image">
          <ProductImage product={product} alt={product.name} />
          <div className="product-badges">
            {onSale ? <span className="badge badge--sale">{discountPct}% OFF</span> : null}
            {product.featured ? <span className="badge badge--featured">Featured</span> : null}
            {product.stock <= 0 ? <span className="badge badge--oos">Out of stock</span> : null}
            {lifecycleBadges.map((b, i) => (
              <span key={`${b.label}-${i}`} className={`badge ${b.className}`}>{b.label}</span>
            ))}
          </div>
          <button
            type="button"
            className="quick-view-btn"
            onClick={handleQuickView}
            aria-label={`Quick view ${product.name}`}
          >
            Quick view
          </button>
        </div>

        <div className="product-info">
          <h3 className="product-name">{product.name}</h3>

          {product.set_name && (
            <p className="product-set">
              {product.set_name}
              {product.card_number ? ` #${product.card_number}` : null}
            </p>
          )}

          {product.rarity && (
            <span className={`rarity-badge rarity-${product.rarity.toLowerCase().replace(/\s+/g, '-')}`}>
              {product.rarity}
            </span>
          )}

          <div className="product-footer">
            {/* Sprint 5: server may strip prices when price_gate_anon is on
                and the caller is anon. price_gated wins over isApproved so
                we never render $null. */}
            {isApproved && !product.price_gated ? (
              <>
                <div className="product-price-block">
                  {onSale && (
                    <span className="product-compare-price">
                      ${Number(product.compare_at_price).toFixed(2)}
                    </span>
                  )}
                  <span className={`product-price${onSale ? ' product-price--sale' : ''}`}>
                    ${Number(product.price).toFixed(2)}
                  </span>
                  <MarketCompare
                    price={product.price}
                    marketPrice={product.market_price}
                    marketPriceAt={product.market_price_at}
                    variant="badge"
                  />
                </div>
                <button
                  className="btn-add-to-cart"
                  onClick={handleAddToCart}
                  disabled={product.stock <= 0}
                >
                  {product.stock <= 0 ? 'Sold out' : 'Add to cart'}
                </button>
              </>
            ) : (
              <>
                <div className="product-price-block" style={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none' }} aria-hidden="true">
                  <span className="product-price">$••.••</span>
                </div>
                <Link
                  to={isLoggedIn ? '/apply' : `/login?next=${encodeURIComponent(window.location.pathname)}`}
                  className="btn-add-to-cart"
                  style={{ background: '#3b4cca', color: '#fff', textDecoration: 'none', textAlign: 'center' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isLoggedIn ? 'Apply for access' : 'Sign in to view price'}
                </Link>
              </>
            )}
          </div>

          {product.stock > 0 && product.stock < 5 && (
            <p className="low-stock">Only {product.stock} left</p>
          )}
          {product.stock >= 10 && (
            <p className="in-stock-pill">In stock</p>
          )}
        </div>
      </Link>
      <QuickView productId={qvOpen ? product.id : null} onClose={() => setQvOpen(false)} />
    </>
  );
};

export default ProductCard;
